import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useAuth } from './AuthContext';
import NotificationService from '../services/NotificationService';
import PushNotificationService from '../services/PushNotificationService';
import pollingManager from '../services/PollingManager';
import browserCacheService from '../services/BrowserCacheService';
import { getWebSocketBaseUrl } from '../utils/websocket';
import { onAuthReset } from '../utils/auth';
import { toast } from '../components/notifications/NotificationToast';
import { haptic } from '../utils/haptics';
import { isToastAllowed, isSoundEnabled } from '../utils/notificationPrefs';
import { playNotificationBeep } from '../utils/notificationSound';
import LevelUpCelebration from '../components/aura/LevelUpCelebration.jsx';
import UnlockLayer from '../components/aura/UnlockLayer.jsx';
import { otterLevelOf } from '../utils/otterWardrobe';

const NotificationContext = createContext(null);

const EMPTY_UNREAD_COUNTS = {
  chat: 0, sessions: 0, connections: 0, general: 0, total: 0, nonChatTotal: 0,
};

/**
 * NotificationProvider - Zentrale State-Verwaltung für alle Notification-Daten
 * Kapselt WebSocket-Verbindung, Polling-Fallback und gruppierte Counts
 */
export const NotificationProvider = ({ children }) => {
  const { user: authUser, getUser } = useAuth();
  const token = authUser?.token || localStorage.getItem('token');
  const userId = authUser?.id;

  const [notifications, setNotifications] = useState([]);
  // Level-Aufstieg, der gerade gefeiert wird ({ oldLevel, newLevel }) - null = keiner.
  const [levelUp, setLevelUp] = useState(null);
  // Otter-Schrank (Aura v2): Anlass zum Nachholen neuer Teile (Reconnect, UNLOCK)
  // und das zuletzt per WebSocket gefeierte Level (gegen Doppelanzeige im Nachholen).
  const [unlockSignal, setUnlockSignal] = useState(0);
  const [celebratedLevel, setCelebratedLevel] = useState(null);
  const hasConnectedRef = useRef(false);
  const getUserRef = useRef(getUser);
  getUserRef.current = getUser;
  const [unreadCounts, setUnreadCounts] = useState(EMPTY_UNREAD_COUNTS);
  const [isLoading, setIsLoading] = useState(false);

  // Benachrichtigungs-Einstellungen ({ email, push, inApp }); null = noch nicht
  // geladen. Der Ref-Spiegel ist noetig, weil handleWsNotification ein
  // useCallback ohne Abhaengigkeiten ist und sonst den Startwert einfriert.
  const [settings, setSettings] = useState(null);
  const settingsRef = useRef(null);

  // Ref-Spiegel der Liste: loadNotifications entscheidet damit über den
  // Spinner, ohne die Callback-Identität an den State zu koppeln
  const notificationsRef = useRef([]);
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Zaehlt jeden Logout/Nutzerwechsel hoch. Eine Antwort, die erst danach eintrifft,
  // gehoert noch zum vorigen Konto und wird verworfen (gleiches Muster wie useAuraConfig).
  const authGenerationRef = useRef(0);

  // Liste, Zaehler, Level-Up und Einstellungen gehoeren dem angemeldeten Konto. Der
  // Provider bleibt beim Logout gemountet (SPA, kein Reload): ohne Reset sah die
  // naechste Person am geteilten Geraet die Benachrichtigungen der vorigen, bis ihr
  // eigener Abruf zurueck war, bei Fehler dauerhaft (Stale-on-Error). onAuthReset
  // laeuft bei clearAuthStorage (Logout, 401, abgelaufene Sitzung) und bei einem
  // Nutzerwechsel ohne Logout.
  useEffect(() => onAuthReset(() => {
    authGenerationRef.current += 1;
    notificationsRef.current = [];
    settingsRef.current = null;
    setNotifications([]);
    setUnreadCounts(EMPTY_UNREAD_COUNTS);
    setLevelUp(null);
    setCelebratedLevel(null);
    setSettings(null);
    setIsLoading(false);
  }), []);

  // Nach App-Start/Reload: gecachte Liste sofort anzeigen, Fetch aktualisiert still
  useEffect(() => {
    if (!userId || notificationsRef.current.length > 0) return;
    const cached = browserCacheService.get(`notifications_${userId}`);
    if (Array.isArray(cached) && cached.length > 0) {
      setNotifications(cached);
    }
  }, [userId]);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const stableResetTimerRef = useRef(null);
  const isCleaningUpRef = useRef(false);
  // Hintergrund-Suspend: Socket wird bei verstecktem Tab / App im Hintergrund
  // getrennt (Akku), beim Zurueckkommen via connectRef wieder aufgebaut
  const isSuspendedRef = useRef(false);
  const hideGraceTimerRef = useRef(null);
  const connectRef = useRef(null);
  // Kein Versuchs-Deckel mehr: Vorher gab die Verbindung nach 5 Versuchen
  // (~62 s) endgueltig auf. Jeder Backend-Neustart (= jeder Deploy) und jeder
  // WLAN-Aussetzer liess offene Tabs dauerhaft ohne Live-Benachrichtigungen
  // zurueck. Jetzt: exponentiell bis maximal 30 s, dann alle 30 s weiter;
  // 'online'-Event und Rueckkehr in den Vordergrund setzen den Zaehler zurueck.
  const RECONNECT_DELAY_BASE = 2000;
  const RECONNECT_DELAY_MAX = 30000;
  const HIDE_GRACE_MS = 30000;

  // ========== DATA FETCHING ==========

  const applySettings = useCallback(next => {
    settingsRef.current = next;
    setSettings(next);
  }, []);

  const refreshSettings = useCallback(async () => {
    const generation = authGenerationRef.current;
    const loaded = await NotificationService.getSettings();
    if (loaded && generation === authGenerationRef.current) applySettings(loaded);
    return loaded;
  }, [applySettings]);

  // Speichert und uebernimmt die vom Backend aufgeloeste Antwort sofort,
  // damit Toast-Filter und Sound ohne Reload auf dem neuen Stand sind.
  const updateSettings = useCallback(async next => {
    const saved = await NotificationService.updateSettings(next);
    if (saved) applySettings(saved);
    return Boolean(saved);
  }, [applySettings]);

  const loadUnreadCounts = useCallback(async () => {
    const generation = authGenerationRef.current;
    try {
      const counts = await NotificationService.getUnreadCounts();
      if (generation !== authGenerationRef.current) return; // vorheriges Konto
      const normalizedCounts = {
        chat: Number(counts?.chat) || 0,
        sessions: Number(counts?.sessions) || 0,
        connections: Number(counts?.connections) || 0,
        general: Number(counts?.general) || 0,
      };
      normalizedCounts.total = Number(counts?.total) || (normalizedCounts.chat + normalizedCounts.sessions + normalizedCounts.connections + normalizedCounts.general);
      normalizedCounts.nonChatTotal = Number(counts?.nonChatTotal) || (normalizedCounts.sessions + normalizedCounts.connections + normalizedCounts.general);

      // Identity-Guard: nur bei echter Wertaenderung setzen, sonst rendert der
      // gesamte Header (fixed + backdrop-blur) bei jedem 90s-Poll unnoetig neu (Flackern)
      setUnreadCounts(prev => {
        const same = prev.chat === normalizedCounts.chat
          && prev.sessions === normalizedCounts.sessions
          && prev.connections === normalizedCounts.connections
          && prev.general === normalizedCounts.general
          && prev.total === normalizedCounts.total
          && prev.nonChatTotal === normalizedCounts.nonChatTotal;
        return same ? prev : normalizedCounts;
      });

      // iOS Badge sofort synchronisieren: wenn keine Chat-Ungelesenen mehr vorhanden sind, Badge leeren
      if (Capacitor.isNativePlatform() && normalizedCounts.chat === 0) {
        PushNotificationService.clearNotifications().catch(() => {});
      }
    } catch (error) {
      console.error('Failed to load unread counts:', error);
    }
  }, []);

  const loadNotifications = useCallback(async (limit = 20) => {
    // Stale-While-Revalidate: Spinner nur beim Kaltstart. Sind schon
    // Notifications da (Context lebt über Routenwechsel, Cache über Reloads),
    // bleibt die Liste sichtbar und der Refresh läuft still im Hintergrund.
    setIsLoading(notificationsRef.current.length === 0);
    const generation = authGenerationRef.current;
    try {
      const data = await NotificationService.getNotifications({ limit });
      // Logout/Nutzerwechsel waehrend des Abrufs: weder anzeigen noch unter der
      // neuen userId in den Cache schreiben
      if (generation !== authGenerationRef.current) return;
      setNotifications(data);
      if (userIdRef.current) {
        browserCacheService.set(`notifications_${userIdRef.current}`, data, browserCacheService.TTL.NOTIFICATIONS);
      }
    } catch (error) {
      // Stale-on-Error: vorhandene (ggf. gecachte) Liste behalten
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ========== WEBSOCKET ==========

  // Use refs so the effect doesn't re-fire when these change
  const tokenRef = useRef(token);
  const userIdRef = useRef(userId);
  tokenRef.current = token;
  userIdRef.current = userId;

  const handleWsNotification = useCallback((messageData) => {
    const { type, title, message, notificationId, data } = messageData;

    // Increment unread counts by category
    setUnreadCounts(prev => {
      const updated = { ...prev, total: prev.total + 1 };
      const normalizedType = type?.replace('notification:', '').toUpperCase();

      if (normalizedType === 'CHAT_MESSAGE') {
        updated.chat = prev.chat + 1;
      } else if (['SESSION_START', 'SESSION_COMPLETED', 'NEW_PUBLIC_SESSION'].includes(normalizedType)) {
        // Bewusst NICHT NOTIFICATION_TAB_TYPES: Dieser Zähler treibt auch den
        // Punkt am Sessions-Reiter der Navigation. Einladungen und Erinnerungen
        // dort mitzuzählen, wäre ein neuer Reiz auf dem Weg zur Beitrittsquote
        // (FF 3). Der Listenfilter der Seite zeigt sie trotzdem.
        updated.sessions = prev.sessions + 1;
        updated.nonChatTotal = (prev.nonChatTotal || 0) + 1;
      } else if (['CHAT_REQUEST', 'CONNECTION_ACCEPTED', 'CONNECTION_DECLINED'].includes(normalizedType)) {
        updated.connections = prev.connections + 1;
        updated.nonChatTotal = (prev.nonChatTotal || 0) + 1;
      } else {
        updated.general = prev.general + 1;
        updated.nonChatTotal = (prev.nonChatTotal || 0) + 1;
      }
      return updated;
    });

    // Add to notification list
    const newNotification = {
      id: notificationId || `temp_${Date.now()}`,
      type: type?.replace('notification:', '').toUpperCase() || 'GENERAL',
      title: title || 'Neue Benachrichtigung',
      message: message || '',
      data: data || {},
      status: 'UNREAD',
      createdAt: new Date().toISOString(),
    };
    setNotifications(prev => [newNotification, ...prev]);

    // 🆙 Level-Aufstieg: Overlay mit dem neuen Otter-Abzeichen (LevelUpCelebration).
    // Bewusst VOR dem Browser-Toast-Block und ohne isToastAllowed: Das Overlay ist
    // ein In-App-Ereignis, kein Push - es erscheint auf Web und nativ, auch wenn
    // "Push bei Aura-Updates" aus ist (Default). Bis September 2026 war ein
    // Level-Up ein generischer Toast, der an genau diesem Schalter hing.
    const isLevelUp = newNotification.type === 'AURA_UPDATE' && newNotification.data?.kind === 'LEVEL_UP';
    if (isLevelUp) {
      const reachedLevel = Number(newNotification.data.newLevel) || 1;
      setLevelUp({
        oldLevel: Number(newNotification.data.oldLevel) || null,
        newLevel: reachedLevel,
      });
      // Das Nachholen (useUnlocks) zeigt dieses Level nicht noch einmal
      setCelebratedLevel(prev => Math.max(prev || 0, reachedLevel));
      // Header-Chip (Level/Punkte) sofort nachziehen - still, ohne Spinner.
      try { getUserRef.current?.(true); } catch { /* silent */ }
    }

    // 📳 Match-Tick (nur nativ): echtes "It's a Match"-Ereignis — jemand hat
    // deine Vernetzungsanfrage angenommen. Diskret, feuert einmal pro Annahme.
    // Bewusst AUSSERHALB des Browser-Toast-Blocks (haptic no-opt im Browser selbst).
    // Der Level-Up-Tick sitzt im Overlay selbst.
    // Neues Otter-Teil: Das Sheet "Neu im Schrank" holt es ab (useUnlocks), auch
    // wenn die App gerade zu war. Deshalb hier kein Toast mehr, nur der Anlass.
    const isUnlock = newNotification.type === 'AURA_UPDATE' && newNotification.data?.kind === 'UNLOCK';
    if (isUnlock) setUnlockSignal(n => n + 1);

    if (newNotification.type === 'CONNECTION_ACCEPTED') {
      haptic.match();
    } else if (newNotification.type === 'AURA_UPDATE' && !isLevelUp) {
      haptic.success();
    }

    // Im Browser: Toast anzeigen (kein natives Push verfügbar)
    // In der nativen App: Push Notification übernimmt die visuelle Anzeige
    if (!Capacitor.isNativePlatform()) {
      const nType = newNotification.type;

      // Der Toast ist im Web das Gegenstueck zum nativen Push — deshalb gelten
      // hier dieselben Schalter ("Push bei neuen Nachrichten" usw.). Bis
      // Sep 2026 erschien im Browser jeder Toast unabhaengig von den Einstellungen.
      if (!isToastAllowed(settingsRef.current, nType)) return;

      let shown = true;
      if (nType === 'CHAT_MESSAGE') {
        toast.info(`${title || 'Neue Nachricht'}\n${(message || '').substring(0, 80)}`, { autoClose: 4000, closeOnClick: true, pauseOnHover: false });
      } else if (nType === 'CONNECTION_ACCEPTED') {
        toast.success(title || 'Vernetzungsanfrage angenommen', { autoClose: 5000, closeOnClick: true });
      } else if (nType === 'CHAT_REQUEST') {
        toast.info(title || 'Neue Vernetzungsanfrage', { autoClose: 5000, closeOnClick: true });
      } else if (nType === 'AURA_UPDATE' && (isLevelUp || isUnlock)) {
        shown = false; // Overlay bzw. Sheet "Neu im Schrank" uebernehmen
      } else if (nType === 'AURA_UPDATE') {
        // Altes Badge: kurzer Toast ohne Overlay.
        // Ohne badgeIcon: das war ein Emoji (CD: keine Emojis ausser 🦦).
        toast.success(`${title || 'Neu bei deiner Aura'}\n${(message || '').substring(0, 80)}`, { autoClose: 5000, closeOnClick: true });
      } else if (nType !== 'CONNECTION_DECLINED') {
        toast.info(`${title || 'Benachrichtigung'}\n${(message || '').substring(0, 80)}`, { autoClose: 4000, closeOnClick: true, pauseOnHover: false });
      } else {
        shown = false;
      }

      if (shown && isSoundEnabled(settingsRef.current)) {
        playNotificationBeep();
      }
    }
  }, []);

  // ========== LIFECYCLE ==========

  useEffect(() => {
    if (!token || !userId) return;

    // Load initial counts + Einstellungen (Toast-Filter, Sound)
    loadUnreadCounts();
    refreshSettings();

    // Polling as fallback (90s)
    pollingManager.register('notificationCounts', loadUnreadCounts, {
      interval: 90000,
      immediate: false,
      errorBackoff: true,
    });

    // Reset state for fresh connection
    isCleaningUpRef.current = false;
    isSuspendedRef.current = false;
    reconnectAttemptsRef.current = 0;
    hasConnectedRef.current = false;

    const getWsUrl = () => {
      const currentToken = tokenRef.current;
      if (!currentToken) return null;
      const base = getWebSocketBaseUrl();
      return `${base}/api/ws/notifications?token=${encodeURIComponent(currentToken)}`;
    };

    const connect = () => {
      if (isCleaningUpRef.current || isSuspendedRef.current) return;

      // Cleanup existing connection without triggering reconnect
      if (wsRef.current) {
        const oldWs = wsRef.current;
        wsRef.current = null;
        oldWs.onclose = null;
        oldWs.onerror = null;
        oldWs.onmessage = null;
        oldWs.onopen = null;
        if (oldWs._heartbeatInterval) clearInterval(oldWs._heartbeatInterval);
        if (stableResetTimerRef.current) clearTimeout(stableResetTimerRef.current);
        try { oldWs.close(); } catch (e) { /* ignore */ }
      }

      try {
        const wsUrl = getWsUrl();
        if (!wsUrl) return;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        // Der Socket ist mit dem Token dieses Kontos angemeldet. Beim Nutzerwechsel ohne
        // Logout bleibt er offen, bis der neue User im State steht (login wartet dazwischen
        // auf /me): was bis dahin ankommt, gehoert dem vorigen Konto.
        const socketGeneration = authGenerationRef.current;

        ws.onopen = () => {
          console.log('Notifications WebSocket connected');
          // Reconnect (nicht der erste Aufbau): verpasste Otter-Teile nachholen.
          // Den Start deckt useUnlocks selbst ab.
          if (hasConnectedRef.current) setUnlockSignal(n => n + 1);
          hasConnectedRef.current = true;
          // Only reset reconnect counter after connection is stable for 10s
          // This prevents infinite reconnect loops when connections drop immediately
          if (stableResetTimerRef.current) clearTimeout(stableResetTimerRef.current);
          stableResetTimerRef.current = setTimeout(() => {
            reconnectAttemptsRef.current = 0;
          }, 10000);
        };

        ws.onmessage = (event) => {
          if (socketGeneration !== authGenerationRef.current) return;
          try {
            const msg = JSON.parse(event.data);
            if (msg.type && msg.type.startsWith('notification:') && msg.type !== 'notification:heartbeat_ack' && msg.type !== 'notification:server_shutdown') {
              handleWsNotification(msg);
            }
          } catch (error) {
            console.error('Error parsing WS notification:', error);
          }
        };

        ws.onerror = () => {
          // Error event has no useful info in browsers, onclose will follow
        };

        ws.onclose = (event) => {
          console.log('Notifications WebSocket closed:', event.code, event.reason);
          // Only reconnect if not intentionally cleaning up
          if (!isCleaningUpRef.current && !isSuspendedRef.current
            && (event.code !== 1000 || !event.reason) && event.code !== 1008) {
            const delay = Math.min(RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttemptsRef.current), RECONNECT_DELAY_MAX);
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current++;
              connect();
            }, delay);
          }
        };

        // Heartbeat every 20s to keep connection alive (faster than backend timeout)
        ws._heartbeatInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'notification:heartbeat' }));
          }
        }, 20000);
      } catch (error) {
        console.error('Failed to create Notifications WebSocket:', error);
      }
    };

    connectRef.current = connect;
    connect();

    return () => {
      // Signal that we're intentionally shutting down
      isCleaningUpRef.current = true;
      connectRef.current = null;

      pollingManager.unregister('notificationCounts');

      // Einstellungen gehoeren zum User — beim Logout/Userwechsel verwerfen
      settingsRef.current = null;

      if (hideGraceTimerRef.current) {
        clearTimeout(hideGraceTimerRef.current);
        hideGraceTimerRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (stableResetTimerRef.current) {
        clearTimeout(stableResetTimerRef.current);
        stableResetTimerRef.current = null;
      }

      if (wsRef.current) {
        const ws = wsRef.current;
        wsRef.current = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.onopen = null;
        if (ws._heartbeatInterval) clearInterval(ws._heartbeatInterval);
        try { ws.close(1000, 'Component unmounting'); } catch (e) { /* ignore */ }
      }
    };
    // Only re-run when token or userId actually change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]);

  useEffect(() => {
    if (!token || !userId) return undefined;

    let appStateListener;

    // Socket + Heartbeat im Hintergrund stoppen (Akku): close(1000, reason)
    // triggert laut onclose-Logik keinen Reconnect
    const suspendWs = () => {
      if (hideGraceTimerRef.current) {
        clearTimeout(hideGraceTimerRef.current);
        hideGraceTimerRef.current = null;
      }
      if (isSuspendedRef.current) return;
      isSuspendedRef.current = true;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (stableResetTimerRef.current) {
        clearTimeout(stableResetTimerRef.current);
        stableResetTimerRef.current = null;
      }
      if (wsRef.current) {
        const ws = wsRef.current;
        wsRef.current = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.onopen = null;
        if (ws._heartbeatInterval) clearInterval(ws._heartbeatInterval);
        try { ws.close(1000, 'app-backgrounded'); } catch (e) { /* ignore */ }
      }
    };

    const resumeWs = () => {
      if (hideGraceTimerRef.current) {
        clearTimeout(hideGraceTimerRef.current);
        hideGraceTimerRef.current = null;
      }
      if (!isSuspendedRef.current) return;
      isSuspendedRef.current = false;
      reconnectAttemptsRef.current = 0;
      connectRef.current?.();
    };

    const handleForegroundSync = () => {
      if (document.hidden) return;
      resumeWs();
      loadUnreadCounts();
    };

    // Web: kurze Tab-Wechsel (<30s) trennen nicht, erst danach wird suspendiert
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (!hideGraceTimerRef.current && !isSuspendedRef.current) {
          hideGraceTimerRef.current = setTimeout(suspendWs, HIDE_GRACE_MS);
        }
      } else {
        handleForegroundSync();
      }
    };

    const registerNativeAppStateListener = async () => {
      if (!Capacitor.isNativePlatform()) return;
      appStateListener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          resumeWs();
          loadUnreadCounts();
        } else {
          // Nativ sofort trennen: iOS friert Timer ohnehin ein, auf Android
          // wuerde der Heartbeat sonst im Hintergrund weiterlaufen
          suspendWs();
        }
      });
    };

    // Netz ist zurueck (WLAN-Wechsel, Funkloch): Backoff-Zaehler nullen und
    // sofort neu verbinden statt bis zu 30 s zu warten.
    const handleOnline = () => {
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (!isSuspendedRef.current && !document.hidden) {
        connectRef.current?.();
      }
    };

    registerNativeAppStateListener();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleForegroundSync);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleForegroundSync);
      window.removeEventListener('online', handleOnline);
      if (hideGraceTimerRef.current) {
        clearTimeout(hideGraceTimerRef.current);
        hideGraceTimerRef.current = null;
      }
      if (appStateListener?.remove) {
        appStateListener.remove();
      }
    };
  }, [token, userId, loadUnreadCounts]);

  // ========== ACTIONS ==========

  const markAsRead = useCallback(async (notificationId) => {
    const success = await NotificationService.markAsRead(notificationId);
    if (success) {
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, status: 'READ' } : n,
      ));
      loadUnreadCounts();
    }
    return success;
  }, [loadUnreadCounts]);

  const markAllAsRead = useCallback(async () => {
    const success = await NotificationService.markAllAsRead({ excludeTypes: ['CHAT_MESSAGE'] });
    if (success) {
      setNotifications(prev => prev.map(n => (n.type === 'CHAT_MESSAGE' ? n : { ...n, status: 'READ' })));
      loadUnreadCounts();
    }
    return success;
  }, [loadUnreadCounts]);

  const refetch = useCallback(() => {
    loadUnreadCounts();
    loadNotifications();
  }, [loadUnreadCounts, loadNotifications]);

  const dismissLevelUp = useCallback(() => setLevelUp(null), []);

  // Nachgeholtes Level (useUnlocks): dasselbe Overlay wie die WebSocket-Meldung
  const showLevelUp = useCallback((next) => {
    if (!next?.newLevel) return;
    setLevelUp({ oldLevel: next.oldLevel || null, newLevel: Number(next.newLevel) || 1 });
    try { getUserRef.current?.(true); } catch { /* silent */ }
  }, []);

  // Performance: useMemo verhindert unnötige Re-Renders aller Consumer-Komponenten
  const value = useMemo(() => ({
    notifications,
    unreadCounts,
    isLoading,
    settings,
    refreshSettings,
    updateSettings,
    markAsRead,
    markAllAsRead,
    loadNotifications,
    refetch,
    levelUp,
    dismissLevelUp,
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
  }), [notifications, unreadCounts, isLoading, settings, refreshSettings, updateSettings, markAsRead, markAllAsRead, loadNotifications, refetch, levelUp, dismissLevelUp]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <LevelUpCelebration levelUp={levelUp} onClose={dismissLevelUp} />
      {/* Neue Otter-Teile (Aura v2): rendert nichts, solange der Schrank der Schule aus
          ist oder nichts Neues da ist. Wartet, bis das Level-Overlay zu ist. */}
      <UnlockLayer
        userId={userId}
        level={otterLevelOf(authUser)}
        refreshSignal={unlockSignal}
        celebratedLevel={celebratedLevel}
        levelUpOpen={Boolean(levelUp)}
        onLevel={showLevelUp}
      />
    </NotificationContext.Provider>
  );
};

/**
 * Hook zum Zugriff auf den NotificationContext
 */
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    // Graceful fallback wenn Provider fehlt (z.B. in Tests oder auf öffentlichen Seiten)
    return {
      notifications: [],
      unreadCounts: EMPTY_UNREAD_COUNTS,
      isLoading: false,
      settings: null,
      refreshSettings: async () => null,
      updateSettings: async () => false,
      markAsRead: async () => false,
      markAllAsRead: async () => false,
      loadNotifications: async () => {},
      refetch: () => {},
      levelUp: null,
      dismissLevelUp: () => {},
      isConnected: false,
    };
  }
  return context;
};

export default NotificationContext;
